import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useGetFaultClientDefinitionsQuery, useCreateFaultClientDefinitionMutation, useUpdateFaultClientDefinitionMutation, useDeleteFaultClientDefinitionMutation } from '../../../../../api/endpoints/faultClientDefinitions';
import { useGetProductGroupsQuery } from '../../../../../api';
import toastService from '../../../../../utils/toastService';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { convertFiltersToPrimeReactFormat, createInitialFilters } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';


const useFaultCustomerDefinitions = () => {

    const { t } = useLanguage();

    const { data: productGroups } = useGetProductGroupsQuery();

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        isActive: 0,
        sortField: null,
        sortOrder: null
    });

    //Pagination
    const [pagination, setPagination] = useState({
        PageNumber: 1,
        PageSize: 10
    });

    const [filterParams, setFilterParams] = useState({});

    const { data, isLoading, isFetching, refetch } = useGetFaultClientDefinitionsQuery({
        PageNumber: lazyParams.page + 1,
        PageSize: lazyParams.rows,
        ...filterParams
    });


    const FAULT_TYPE_OPTIONS = [
        { label: t('warranty.faultCustomerDefinitions.dialog.faultType1'), value: 1 },
        { label: t('warranty.faultCustomerDefinitions.dialog.faultType2'), value: 2 }
    ];

    //FILTER CONST
    // Define filter types and fields with their match modes
    const filterTypeMap = useMemo(
        () => ({
            productGroup: {
                type: 'select',
                matchMode: FilterMatchMode.EQUALS,
                options: productGroups?.data || [] // düzeltildi
            },
            faultType: {
                type: 'select',
                matchMode: FilterMatchMode.EQUALS,
                options: FAULT_TYPE_OPTIONS
            },
            startDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            endDate: { type: 'date', matchMode: FilterMatchMode.DATE_BEFORE },
            isActive: { type: 'boolean', matchMode: FilterMatchMode.EQUALS }
        }),
        [productGroups]
    );
    // Create initial filters - array of filter field names
    const filterFieldNames = useMemo(() => ['productGroup', 'faultType', 'startDate', 'endDate', 'isActive'], []);
    // Create initial filters - set isActive to true by default
    const initialFilters = useMemo(() => {
        const baseFilters = createInitialFilters(filterFieldNames, filterTypeMap);
        // Set default value for isActive filter to true
        baseFilters.isActive = { value: true, matchMode: FilterMatchMode.EQUALS };
        return baseFilters;
    }, [filterFieldNames, filterTypeMap]);
    const [filters, setFilters] = useState(initialFilters);

    // Create a debounce function
    const useDebounce = (callback, delay) => {
        const timeoutRef = useRef(null);

        return useCallback((...args) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callback(...args);
                timeoutRef.current = null;
            }, delay);
        }, [callback, delay]);
    };



    const [createFaultClientDefinition, { isLoading: isCreating }] = useCreateFaultClientDefinitionMutation();
    const [updateFaultClientDefinition, { isLoading: isUpdating }] = useUpdateFaultClientDefinitionMutation();
    const [deleteFaultClientDefinition, { isLoading: isDeleting }] = useDeleteFaultClientDefinitionMutation();



    // Sadece sıralama için kullanılacak - Filtreleme API'de yapılıyor
    const sortedData = useMemo(() => {
        const items = Array.isArray(data) ? data : data?.items || [];
        if (!lazyParams.sortField) return items;

        return [...items].sort((a, b) => {
            let aVal, bVal;

            // Translation fields için özel işlem
            if (lazyParams.sortField === 'trText') {
                aVal = a.translations?.find(t => t.languageId == 2)?.text || '';
                bVal = b.translations?.find(t => t.languageId == 2)?.text || '';
            } else if (lazyParams.sortField === 'enText') {
                aVal = a.translations?.find(t => t.languageId == 1)?.text || '';
                bVal = b.translations?.find(t => t.languageId == 1)?.text || '';
            }
            // else if (lazyParams.sortField.toLowerCase().includes('date') || lazyParams.sortField === 'sellDate') {
            //     aVal = new Date(aVal);
            //     bVal = new Date(bVal);
            // }
            else {
                aVal = a[lazyParams.sortField] || '';
                bVal = b[lazyParams.sortField] || '';
            }

            const order = lazyParams.sortOrder === 1 ? 1 : -1;
            return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
        });
    }, [data, lazyParams.sortField, lazyParams.sortOrder]);


    //FILTER
    // Update filters in a stable way without creating unnecessary new objects
    const updateFilters = useCallback((key, value) => {
        setFilters(prevFilters => {
            // Create a shallow copy only if we're changing something
            if (key === 'global') {
                const newGlobal = { ...prevFilters.global, value };
                if (JSON.stringify(newGlobal) === JSON.stringify(prevFilters.global)) {
                    return prevFilters; // No change needed
                }
                return { ...prevFilters, global: newGlobal };
            } else {
                // For other filter types
                const currentFilter = prevFilters[key];
                if (!currentFilter) return prevFilters;

                if (key === 'isActive') {
                    const newFilter = { value, matchMode: FilterMatchMode.EQUALS };
                    if (JSON.stringify(newFilter) === JSON.stringify(currentFilter)) {
                        return prevFilters; // No change needed
                    }
                    return { ...prevFilters, [key]: newFilter };
                } else {
                    const newConstraints = [{ value, matchMode: FilterMatchMode.STARTS_WITH }];
                    const newFilter = {
                        operator: FilterOperator.AND,
                        constraints: newConstraints
                    };

                    if (JSON.stringify(newFilter) === JSON.stringify(currentFilter)) {
                        return prevFilters; // No change needed
                    }
                    return { ...prevFilters, [key]: newFilter };
                }
            }
        });
    }, []);

    // Create debounced version of updateFilters
    const debouncedUpdateFilters = useDebounce(updateFilters, 300);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    // Handle global filter change
    const onGlobalFilterChange = useCallback((e) => {
        const value = e.target.value;
        setGlobalFilterValue(value);

        // Debounced filter update
        debouncedUpdateFilters('global', value);
    }, [debouncedUpdateFilters]);

    // Define filter fields manually
    const filterFields = useMemo(
        () => [
            {
                field: 'faultType',
                headerName: t('warranty.faultCustomerDefinitions.filter.faultType'),
                type: 'select',
                options: FAULT_TYPE_OPTIONS
            },
            {
                field: 'productGroup',
                headerName: t('warranty.faultCustomerDefinitions.filter.productGroup'),
                type: 'select',
                options: productGroups?.data || []
            },

            {
                field: 'startDate',
                headerName: t('warranty.faultCustomerDefinitions.filter.startDate'),
                type: 'date'
            },
            {
                field: 'endDate',
                headerName: t('warranty.faultCustomerDefinitions.filter.endDate'),
                type: 'date'
            },
            {
                field: 'isActive',
                headerName: t('warranty.faultCustomerDefinitions.filter.status'),
                type: 'boolean'
            }
        ],
        [productGroups]
    );

    // Define custom filter types
    const customFilterTypes = useMemo(
        () => ({
            contractStartDate: 'date', // Use date filter for start date
            contractEndDate: 'date', // Use date filter for end date
            isActive: 'boolean' // Use boolean filter for status
        }),
        []
    );

    // Define custom filter options
    const customFilterOptions = useMemo(
        () => ({
            isActive: [
                { label: t('warranty.faultCustomerDefinitions.filter.active'), value: true },
                { label: t('warranty.faultCustomerDefinitions.filter.inactive'), value: false }
            ]
        }),
        []
    );



    useEffect(() => {
        refetch();
    }, [filters, lazyParams]);



    //
    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page
        }));
    };


    const handleFilterChange = useCallback((updatedFilters) => {

        const apiParams = {};

        if (updatedFilters.productGroup) {
            apiParams.ProductGroup = updatedFilters.productGroup.value || updatedFilters.productGroup;
        }

        if (updatedFilters.faultType) {
            apiParams.FaultType = updatedFilters.faultType.value || updatedFilters.faultType;
        }

        if (updatedFilters.startDate instanceof Date) {
            apiParams.StartDate = updatedFilters.startDate.toISOString();
        }

        if (updatedFilters.endDate instanceof Date) {
            apiParams.EndDate = updatedFilters.endDate.toISOString();
        }

        if (updatedFilters.isActive !== undefined) {
            apiParams.IsActive = updatedFilters.isActive ? 1 : 0;
        }

        setFilterParams(apiParams);

        setPagination(prev => ({
            PageNumber: 1,
            PageSize: prev.PageSize,
            ...apiParams
        }));

        setTimeout(() => {
            refetch();
        }, 100);
    }, [refetch]);

    // Reset global filter value
    const resetGlobalFilter = useCallback(() => {
        setGlobalFilterValue('');
        // Also reset the global filter in the filters state
        setFilters(prev => ({
            ...prev,
            global: { value: null, matchMode: FilterMatchMode.CONTAINS }
        }));
    }, []);

    // Reset filters
    const handleResetFilters = useCallback(() => {

        // Return to default filters - set isActive value to true
        const resetFilters = createInitialFilters(filterFieldNames, filterTypeMap);
        resetFilters.isActive = { value: true, matchMode: FilterMatchMode.EQUALS };

        // Ensure all filter objects have the correct structure based on their type
        Object.keys(resetFilters).forEach(key => {
            if (key !== 'global' && key !== 'isActive') {
                const filterType = filterTypeMap[key] || 'text';

                switch (filterType) {
                    case 'boolean':
                    case 'autoComplete':
                        resetFilters[key] = { value: null, matchMode: FilterMatchMode.EQUALS };
                        break;
                    case 'number':
                    case 'date':
                    case 'text':
                    default:
                        resetFilters[key] = {
                            operator: FilterOperator.AND,
                            constraints: [{ value: null, matchMode: filterType === 'text' ? FilterMatchMode.STARTS_WITH : FilterMatchMode.EQUALS }]
                        };
                        break;
                }
            }
        });

        // Reset PrimeReact DataTable filters
        setFilters(resetFilters);

        // Reset global filter value
        resetGlobalFilter();

        setFilterParams({});

        // Force a new API request by calling refetch
        setTimeout(() => {
            refetch();
        }, 100);

    }, [filterFieldNames, filterTypeMap, resetGlobalFilter, refetch]);

    const handleApplyFilters = useCallback((updatedFilters) => {


        // 1. PrimeReact için filtreleri güncelle
        const convertedFilters = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
        setFilters(convertedFilters);

        // 2. API'ye gönderilecek filtre parametrelerini hazırla
        const apiParams = {};

        if (updatedFilters.productGroup)
            apiParams.ProductGroup = updatedFilters.productGroup.value || updatedFilters.productGroup;

        if (updatedFilters.faultType)
            apiParams.FaultType = updatedFilters.faultType.value || updatedFilters.faultType;

        if (updatedFilters.startDate)
            apiParams.StartDate = updatedFilters.startDate instanceof Date
                ? updatedFilters.startDate.toISOString()
                : new Date(updatedFilters.startDate).toISOString();

        if (updatedFilters.endDate)
            apiParams.EndDate = updatedFilters.endDate instanceof Date
                ? updatedFilters.endDate.toISOString()
                : new Date(updatedFilters.endDate).toISOString();

        if (updatedFilters.isActive !== undefined)
            apiParams.IsActive = updatedFilters.isActive ? 1 : 0;

        // 3. pagination state’ini güncelle
        setFilterParams(apiParams);

        // 4. Yeniden fetch
        setTimeout(() => {
            refetch();
        }, 100);
    }, [filters, filterTypeMap, refetch]);




    // Sıralama olayını işle
    const onSort = (event) => {
        setLazyParams(prev => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };



    const handleCreate = async (formData) => {
        try {
            const response = await createFaultClientDefinition(formData).unwrap();
            if (response.isSuccessful) {
                toastService.success(t('messages.saveSuccess'));
                await refetch();
                return true;
            } else {
                toastService.error(response.errorMessage || t('messages.saveError'));
                return false;
            }
        } catch (error) {
            toastService.error(t('messages.saveError'));
            return false;
        }
    };

    const handleUpdate = async (formData) => {
        try {
            const response = await updateFaultClientDefinition(formData).unwrap();
            if (response.isSuccessful) {
                toastService.success(t('messages.updateSuccess'));
                await refetch();
                return true;
            } else {
                toastService.error(response.errorMessage || t('messages.updateError'));
                return false;
            }

        } catch (error) {
            toastService.error(t('messages.updateError'));
            return false;
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await deleteFaultClientDefinition(id).unwrap();
            if (response.isSuccessful) {
                toastService.success(t('messages.deleteSuccess'));
                await refetch();
                return true;
            } else {
                toastService.error(response.errorMessage || t('messages.deleteError'));
                return false;
            }
        } catch (error) {
            toastService.error(t('messages.deleteError'));
            return false;
        }
    };

    return {
        data: {
            items: sortedData,
            totalCount: data?.totalCount || 0
        },
        originalData: data,
        loading: isLoading || isFetching,
        lazyParams,

        // Filter related
        filterFields,
        customFilterTypes,
        customFilterOptions,


        filters,
        globalFilterValue,
        productGroups: productGroups?.data || [],
        handleFilterChange,
        handleApplyFilters,
        handleResetFilters,
        onPage,
        onSort,
        handleCreate,
        handleUpdate,
        handleDelete,
        onGlobalFilterChange,
        FAULT_TYPE_OPTIONS
    };
};

export default useFaultCustomerDefinitions; 